// Edit Role Page - Roles & Permissions Management
import React, { useState, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AccessRestricted } from '@/components/shared/AccessRestricted';
import { Tabs } from '@/components/ui/tabs';
import { EditRoleTabsList } from '@/features/roles/components/edit-role/EditRoleTabsList';
import { EditRoleTabsContent } from '@/features/roles/components/edit-role/EditRoleTabsContent';
import { SaveConfirmationDialog, TransferUserDialog } from '@/features/roles/components/edit-role/dialogs';
import { buildAdminSubSettings, buildSmartFeatureItems } from '@/features/roles/components/edit-role/tabItems';
import {
  usePermissionState,
  useBulkActions,
  useEditRoleData,
  useRoleSave,
  useUnsavedChangesGuard,
  useTransferUser,
  buildPermissionStateFromDefaults,
  getIcon,
  getCategorySummaryCount,
  getTabPermissionCount,
  getTotalPermissionsCount,
  type PermissionState,
} from '@/features/roles/components/edit-role/hooks';
import { permissionCategories } from '@/features/roles/components/edit-role/permissionCategories';
import { PageHeader } from '@/components/blocks/page-header';
import { toast } from 'sonner';
import { useGlobalSettings } from '@/hooks/use-global-settings';
import { useDataSource } from '@/contexts/DataSourceContext';
import { usePermissions } from '@/contexts/PermissionContext';
import { getUserRoleService } from '@/services/userRoleService';
import { DEFAULT_ROLE_PERMISSIONS_MAP } from '@/services/defaultRolePermissions';

interface EditRolePageProps {
  onNavigate?: (path: string) => void;
}

export const EditRolePage: React.FC<EditRolePageProps> = ({ onNavigate }) => {
  const { roleId } = useParams<{ roleId: string }>();
  const navigate = useNavigate();
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [activeTab, setActiveTab] = useState<string>('details');
  const [nameError, setNameError] = useState<string>('');

  const { permissions, setPermission, setAllPermissions } = usePermissionState();

  const globalSettings = useGlobalSettings();
  const { getScreenMode } = useDataSource();
  const { canManageRolesPermissions } = usePermissions();
  const screenMode = getScreenMode('roles-permissions');
  const userRoleService = useMemo(() => getUserRoleService(screenMode), [screenMode]);
  const isCreateMode = !roleId || roleId === 'create';

  const {
    roleName,
    setRoleName,
    roleDescription,
    setRoleDescription,
    isSystem,
    isActive,
    setIsActive,
    users,
    setUsers,
    allRoles,
    accessDenied,
    initialSnapshotRef,
  } = useEditRoleData({ roleId, isCreateMode, userRoleService, setAllPermissions });

  // Only the Administrator platform role has 'Manage Roles & Permissions' locked on.
  // All other roles (including the User/Approver system roles) default to unticked
  // and can be granted the permission when General Settings access is 'Configure'.
  const isAdministrator = isSystem && roleName === 'Administrator';

  const markUnsavedChanges = useCallback(() => setHasUnsavedChanges(true), []);

  const updatePermission = useCallback(
    <K extends keyof PermissionState>(key: K, value: PermissionState[K]) => {
      setPermission(key, value);
      setHasUnsavedChanges(true);
    },
    [setPermission],
  );

  const applyPermissions = useCallback(
    (next: Partial<PermissionState>) => {
      setAllPermissions(next);
      setHasUnsavedChanges(true);
    },
    [setAllPermissions],
  );

  // ── Unsaved changes navigation guard ─────────────────────────────────────
  const { confirmNavigation: confirmNav } = useUnsavedChangesGuard(hasUnsavedChanges);

  // ── Bulk actions & derived counts ─────────────────────────────────────────
  const bulkActions = useBulkActions({
    permissions,
    setAllPermissions,
    onPermissionChange: markUnsavedChanges,
    globalSettings,
    isAdministrator,
  });

  const totalPermissionsCount = getTotalPermissionsCount(permissions);
  const summaryCountForCategory = useCallback(
    (categoryId: string) => getCategorySummaryCount(categoryId, permissions),
    [permissions],
  );
  const tabPermissionCount = useCallback(
    (tab: string) => getTabPermissionCount(tab, permissions),
    [permissions],
  );

  const detailsCategories = useMemo(
    () => permissionCategories.map(({ id, name, iconName }) => ({ id, name, icon: getIcon(iconName, 'h-4 w-4') })),
    [],
  );

  const smartFeatureItems = buildSmartFeatureItems(permissions, globalSettings, updatePermission);
  const adminSubSettings = buildAdminSubSettings(permissions, updatePermission);

  // ── Save & transfer ───────────────────────────────────────────────────────
  const navigateToRoles = useCallback(() => {
    if (onNavigate) onNavigate('/settings/roles-permissions');
    else navigate('/settings/roles-permissions');
  }, [onNavigate, navigate]);

  const {
    isSaving,
    showSaveConfirmation,
    setShowSaveConfirmation,
    permissionChanges,
    performSave,
    handleSave,
  } = useRoleSave({
    roleId,
    isCreateMode,
    userRoleService,
    permissions,
    isAdministrator,
    roleName,
    roleDescription,
    isActive,
    totalPermissionsCount,
    initialSnapshotRef,
    setNameError,
    setActiveTab,
    setHasUnsavedChanges,
    onSaved: navigateToRoles,
  });

  const {
    transferDialogOpen,
    setTransferDialogOpen,
    selectedUser,
    newRoleId,
    setNewRoleId,
    handleTransferRole,
    handleConfirmTransfer,
  } = useTransferUser({ roleId, userRoleService, setUsers });

  const handleCancel = () => confirmNav(navigateToRoles);

  /**
   * Apply the default permission template for a system role.
   * Only available for system roles (ids 1, 2, 3 = Administrator, User, Approver).
   * Populates all permission state from the DEFAULT_ROLE_PERMISSIONS_MAP constant.
   */
  const handleApplyDefaultTemplate = useCallback(() => {
    if (!roleId) return;
    const defaults = DEFAULT_ROLE_PERMISSIONS_MAP[Number(roleId)];
    if (!defaults) return;
    applyPermissions(buildPermissionStateFromDefaults(defaults));
    toast.success('Default template applied. Review and save to confirm.');
  }, [roleId, applyPermissions]);

  // ── Render ────────────────────────────────────────────────────────────────

  // Permission gate — show friendly message when user lacks access
  if (!canManageRolesPermissions() || accessDenied) {
    return (
      <div className="space-y-6">
        <PageHeader
          title={isCreateMode ? 'Create New Role' : 'Edit Role'}
          size={36}
          showActions={false}
          showBreadcrumb={false}
          icon={<Shield />}
        />
        <AccessRestricted message="You don't have permission to manage Roles & Permissions. Contact your administrator to request access." />
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-24">

      {/* Page Header */}
      <PageHeader
        title={isCreateMode ? 'Create New Role' : (roleName || 'Edit Role')}
        subtitle={isCreateMode ? 'Set up a new role with specific permissions and access levels' : 'Configure role details and permissions'}
        size={36}
        showBreadcrumb={false}
      />

      {/* Unsaved Changes Alert */}
      {hasUnsavedChanges && (
        <Alert variant="warning" showIcon={false}>
          <AlertDescription>
            You have unsaved changes. Click "{isCreateMode ? 'Create Role' : 'Save Settings'}" at the bottom to apply your changes.
          </AlertDescription>
        </Alert>
      )}

      {/* Platform role info */}
      {isSystem && (
        <Alert showIcon={false}>
          <AlertDescription>
            <span className="font-medium">Platform Role</span> — You can modify permissions but cannot delete this role.
          </AlertDescription>
        </Alert>
      )}

      {/* Tabbed Interface */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <EditRoleTabsList usersCount={users.length} getTabPermissionCount={tabPermissionCount} />
        <EditRoleTabsContent
          permissions={permissions}
          updatePermission={updatePermission}
          applyPermissions={applyPermissions}
          bulkActions={bulkActions}
          smartFeatureItems={smartFeatureItems}
          adminSubSettings={adminSubSettings}
          isAdministrator={isAdministrator}
          users={users}
          roleName={roleName}
          onTransferUser={handleTransferRole}
          detailsProps={{
            roleName,
            onRoleNameChange: (v) => {
              setRoleName(v);
              setHasUnsavedChanges(true);
              if (!v.trim()) setNameError('Role name is required');
              else if (v.trim().length < 2) setNameError('Role name must be at least 2 characters');
              else setNameError('');
            },
            roleDescription,
            onRoleDescriptionChange: (v) => { setRoleDescription(v); setHasUnsavedChanges(true); },
            isActive,
            onIsActiveChange: (v) => { setIsActive(v); setHasUnsavedChanges(true); },
            isSystem,
            nameError,
            usersCount: users.length,
            totalPermissionsCount,
            categories: detailsCategories,
            getCategorySummaryCount: summaryCountForCategory,
            onViewUsers: () => setActiveTab('users'),
          }}
        />
      </Tabs>

      <SaveConfirmationDialog
        open={showSaveConfirmation}
        onOpenChange={setShowSaveConfirmation}
        roleName={roleName}
        usersAffectedCount={users.length}
        totalPermissionsCount={totalPermissionsCount}
        permissionChanges={permissionChanges}
        isSaving={isSaving}
        onConfirm={performSave}
        onCancel={() => setShowSaveConfirmation(false)}
      />

      <TransferUserDialog
        open={transferDialogOpen}
        onOpenChange={setTransferDialogOpen}
        user={selectedUser}
        currentRoleName={roleName}
        availableRoles={allRoles.filter(role => String(role.id) !== roleId)}
        newRoleId={newRoleId}
        onNewRoleIdChange={setNewRoleId}
        onConfirm={handleConfirmTransfer}
        onCancel={() => setTransferDialogOpen(false)}
      />

      {/* Sticky Footer */}
      <div className="fixed bottom-0 z-10 border-t bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60" style={{ left: 'var(--sidebar-width)', right: 0 }}>
        <div className="flex h-16 items-center justify-end gap-3 px-6 max-w-[2000px] mx-auto">
          <Button variant="outline" size="lg" className="min-w-[120px]" onClick={handleCancel}>Cancel</Button>
          <Button size="lg" className="min-w-[120px]" onClick={handleSave} disabled={isCreateMode ? false : !hasUnsavedChanges}>
            {isCreateMode ? 'Create Role' : 'Save Settings'}
          </Button>
        </div>
      </div>
    </div>
  );
};
